import React, { useState } from "react";
import { Image as ImageIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { responsiveImage } from "@/lib/images";
import { RESULT_ASIDE, RESULT_BODY, RESULT_FRAME, RESULT_MEDIA } from "./resultCardLayout";

/**
 * The shell every search result shares (stays, cars, flights, taxis,
 * activities). It owns the frame, the order and the responsive layout; each
 * vertical composes its own facts into it.
 *
 *   <ResultCard>
 *     <ResultCard.Media src={photo} icon={Bed} />
 *     <ResultCard.Body>
 *       <ResultCard.Title><ResultCard.Link as={Link} to={url}>Casa do Rio</ResultCard.Link></ResultCard.Title>
 *       <ResultCard.Meta>Alfama · 900 m du centre</ResultCard.Meta>
 *       <ResultCard.Badges>…</ResultCard.Badges>
 *       <ResultCard.Terms>…</ResultCard.Terms>
 *     </ResultCard.Body>
 *     <ResultCard.Aside>
 *       <Price align="responsive" … />
 *       <ResultCard.Action>…</ResultCard.Action>
 *     </ResultCard.Aside>
 *   </ResultCard>
 *
 * Phones: photo, then the facts, then the price row (total at the start, the
 * button at the end). From 640 px: one row, price column at the end.
 * One link (the title) covers the whole card; the Action sits above it and
 * keeps its own target, so a card has exactly two tab stops. A flight leaves
 * out Media. Bordered, no shadow, no lift on hover.
 */
function ResultCardRoot({ as: Root = "article", className, children, ...rest }) {
  return (
    <Root className={cn(RESULT_FRAME, "transition-colors duration-150 hover:border-[color:var(--touki-field,#8A94A6)]", className)} {...rest}>
      {children}
    </Root>
  );
}

/**
 * The listing's real photo, or a neutral plate with the product's icon when
 * there is none (or it fails to load). Never a stock picture in its place.
 * `priority` for the first card on screen (the page's largest image: fetched
 * at once, ahead of the rest); `loading="eager"` for the next ones a page
 * knows are in the first screen (fetched at once, without the high priority).
 */
function Media({ src, alt = "", icon: Icon = ImageIcon, priority = false, loading, sizes = "(min-width: 1024px) 196px, (min-width: 640px) 160px, 100vw", className }) {
  // Remembers which address failed, so a new photo gets its own chance.
  const [failedSrc, setFailedSrc] = useState(null);
  const show = Boolean(src) && failedSrc !== src;
  const image = show ? responsiveImage(src, [400, 800], sizes) : null;
  return (
    // Without a photo the plate is lower on a phone: a pale band, not a
    // large empty frame.
    <div className={cn(RESULT_MEDIA, !show && "max-sm:aspect-[3/1]", className)} data-testid="result-media">
      {show ? (
        <img {...image} alt={alt} width={400} height={225} loading={priority ? "eager" : loading || "lazy"} decoding="async"
          fetchPriority={priority ? "high" : undefined} onError={() => setFailedSrc(src)}
          className="h-full w-full object-cover outline outline-1 -outline-offset-1 outline-touki-black/10" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-touki-muted" data-testid="result-media-plate">
          <Icon size={32} weight="regular" aria-hidden="true" focusable="false" />
        </div>
      )}
    </div>
  );
}

function Body({ className, children }) {
  return <div className={cn(RESULT_BODY, className)}>{children}</div>;
}

/** The name. `as` keeps the page's heading order (h3 under a results h2 by default). */
function Title({ as: Heading = "h3", className, children }) {
  return <Heading className={cn("text-heading-md font-bold text-touki-black text-balance", className)}>{children}</Heading>;
}

/**
 * The card's one link. Its hit area is stretched over the whole card. `as`
 * takes a router Link (with `to`); a plain <a href> otherwise.
 */
function CardLink({ as: Anchor = "a", className, children, ...rest }) {
  return (
    <Anchor data-result-link="" className={cn("text-inherit no-underline hover:underline underline-offset-4 after:absolute after:inset-0 after:rounded-lg after:content-['']", className)} {...rest}>
      {children}
    </Anchor>
  );
}

function Meta({ className, children }) {
  return <p className={cn("text-body-sm text-touki-muted text-pretty", className)}>{children}</p>;
}

function Badges({ className, children }) {
  return <div className={cn("flex flex-wrap items-center gap-2", className)}>{children}</div>;
}

/** Conditions that belong next to the price: the cancellation deadline, how payment works. */
function Terms({ className, children }) {
  return <div className={cn("mt-auto pt-1 text-body-sm text-touki-black text-pretty", className)}>{children}</div>;
}

function Aside({ className, children }) {
  return <div className={cn(RESULT_ASIDE, className)}>{children}</div>;
}

/**
 * The button or link that acts on this result, above the card's stretched
 * link. `duplicate`: it only repeats the card's own link (a "See rooms" that
 * opens the same page): it stays a visible, clickable target, but is kept out
 * of the tab order and of what a screen reader lists, so the card is one
 * link, heard once.
 */
function Action({ className, duplicate = false, children }) {
  if (!duplicate) return <div className={cn("relative z-[1] shrink-0", className)}>{children}</div>;
  return (
    <div className={cn("relative z-[1] shrink-0", className)} aria-hidden="true">
      {React.Children.map(children, (child) => (React.isValidElement(child) ? React.cloneElement(child, { tabIndex: -1 }) : child))}
    </div>
  );
}

const ResultCard = Object.assign(ResultCardRoot, { Media, Body, Title, Link: CardLink, Meta, Badges, Terms, Aside, Action });
export default ResultCard;
